// src/components/CollectionsReport.js
// Daily Collections: payment receipts summarised by day and by bank for a period
// — the bursar's daily cash/bank reconciliation view.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, MenuItem, Alert, CircularProgress, IconButton, Grid
} from '@mui/material';
import { Home, Print, Payments, Description } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import CompanyHeader from './CompanyHeader';
import { useCompany } from '../hooks/useCompany';
import { downloadCsv } from '../utils/csvExport';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const todayStr     = () => new Date().toISOString().split('T')[0];

const CollectionsReport = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [from, setFrom]       = useState(firstOfMonth());
  const [to, setTo]           = useState(todayStr());
  const [bank, setBank]       = useState('');
  const [banks, setBanks]     = useState([]);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    axios.get(`${API}/api/finance/banks`, { headers: hdrs() })
      .then(r => setBanks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { from, to };
      if (bank) params.bankAccountNo = bank;
      const { data } = await axios.get(`${API}/api/finance/reports/collections`, { headers: hdrs(), params });
      setData(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load collections'); }
    finally { setLoading(false); }
  }, [from, to, bank]);

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportExcel = () => {
    if (!data) return;
    const rows = [
      [company?.name || 'DAILY COLLECTIONS'],
      ['DAILY COLLECTIONS'],
      [`Period: ${from} to ${to}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      ['BY DAY'],
      ['Date', 'Receipts', 'Amount'],
      ...data.byDay.map(d => [d.date, d.count, d.amount]),
      ['TOTAL', data.total.count, data.total.amount],
      [],
      ['BY BANK'],
      ['Bank', 'Account', 'Receipts', 'Amount'],
      ...data.byBank.map(b => [b.bankName, b.accountName, b.count, b.amount]),
    ];
    downloadCsv(rows, `collections_${from}_to_${to}.csv`);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } .col-card { box-shadow:none !important; } }`}</style>
      <Box display="flex" gap={1.5} mb={2} className="no-print" alignItems="center" flexWrap="wrap">
        <TextField type="date" label="From" value={from} size="small"
          onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" label="To" value={to} size="small"
          onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField select label="Bank" value={bank} size="small" sx={{ minWidth: 160 }}
          onChange={e => setBank(e.target.value)}>
          <MenuItem value=""><em>All banks</em></MenuItem>
          {banks.map(b => <MenuItem key={b.accountNo} value={b.accountNo}>{b.bankName} — {b.accountName}</MenuItem>)}
        </TextField>
        <IconButton onClick={run} disabled={loading} color="primary" sx={{ bgcolor: '#2e7d3215' }}>
          {loading ? <CircularProgress size={20} /> : <Payments />}
        </IconButton>
        <Box flex={1} />
        <IconButton onClick={exportExcel} title="Export to Excel" disabled={!data}
          sx={{ bgcolor: 'background.paper', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}><Description /></IconButton>
        <IconButton onClick={() => window.print()} title="Print" disabled={!data}
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Print /></IconButton>
        <IconButton onClick={() => navigate('/finance')} title="Finance Home"
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card elevation={3} className="col-card">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <CompanyHeader title="DAILY COLLECTIONS" subtitle={`${from} → ${to}`} />
          {data && (
            <>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'success.light', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Total Collected ({data.total.count} receipts)</Typography>
                <Typography variant="h5" fontWeight="bold">{sym()} {fmt(data.total.amount)}</Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>By Day</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell align="right"><strong>Receipts</strong></TableCell>
                        <TableCell align="right"><strong>Amount</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.byDay.length === 0 ? (
                        <TableRow><TableCell colSpan={3} align="center">No receipts</TableCell></TableRow>
                      ) : data.byDay.map(d => (
                        <TableRow key={d.date} hover>
                          <TableCell>{d.date}</TableCell>
                          <TableCell align="right">{d.count}</TableCell>
                          <TableCell align="right">{fmt(d.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>By Bank</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Bank</strong></TableCell>
                        <TableCell align="right"><strong>Receipts</strong></TableCell>
                        <TableCell align="right"><strong>Amount</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.byBank.length === 0 ? (
                        <TableRow><TableCell colSpan={3} align="center">No receipts</TableCell></TableRow>
                      ) : data.byBank.map(b => (
                        <TableRow key={b.bankAccountNo} hover>
                          <TableCell>{b.bankName}</TableCell>
                          <TableCell align="right">{b.count}</TableCell>
                          <TableCell align="right">{fmt(b.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CollectionsReport;
